import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Clock, ArrowRight, MessageCircle } from 'lucide-react';
import SEO from '../components/SEO';
import { BLOG_POSTS } from '../data/blogData';

const Blog = () => {
  const handleWhatsApp = () => {
    const message = 'Hello, I want to know about available puppies at Petsmart Puppies.';
    window.open(`https://wa.me/917217648679?text=${encodeURIComponent(message)}`, '_blank');
  };

  return (
    <>
      <SEO path="/blog" />

      <div data-testid="blog-page" style={{ backgroundColor: 'var(--neutral-bg)', minHeight: '100vh' }}>
        {/* Hero Section */}
        <section
          className="py-12 sm:py-16 lg:py-20"
          style={{
            background: 'linear-gradient(to bottom right, #E0F2FE, #FFFFFF)',
            borderBottom: '1px solid var(--neutral-border)'
          }}
        >
          <div className="container mx-auto px-4">
            <div className="text-center max-w-3xl mx-auto">
              <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold mb-4" style={{ color: 'var(--text-main)' }}>
                Pet Care Blog
              </h1>
              <p className="text-base sm:text-lg mb-6" style={{ color: 'var(--text-muted)' }}>
                Expert tips on puppy care, breed guides, training advice, and more from Noida's trusted pet store
              </p>
              <div className="flex flex-wrap justify-center gap-3">
                <span className="px-4 py-2 rounded-full text-sm font-medium" style={{ backgroundColor: 'var(--primary-light)', color: 'var(--primary-dark)' }}>
                  Breed Guides
                </span>
                <span className="px-4 py-2 rounded-full text-sm font-medium" style={{ backgroundColor: 'var(--secondary-light)', color: 'var(--secondary-dark)' }}>
                  Pet Care
                </span>
                <span className="px-4 py-2 rounded-full text-sm font-medium" style={{ backgroundColor: 'var(--accent-light)', color: 'var(--accent-dark)' }}>
                  Training Tips
                </span>
              </div>
            </div>
          </div>
        </section>

        {/* Blog Posts Grid */}
        <section className="py-12 sm:py-16">
          <div className="container mx-auto px-4">
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6 sm:gap-8">
              {BLOG_POSTS.map((post, index) => (
                <motion.article
                  key={post.slug}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: index * 0.05 }}
                  viewport={{ once: true }}
                  className="group"
                >
                  <Link to={`/blog/${post.slug}/`} className="block">
                    <div
                      className="shadow-soft transition-all hover:shadow-xl overflow-hidden"
                      style={{
                        backgroundColor: 'white',
                        borderRadius: '1rem'
                      }}
                    >
                      {/* Image */}
                      <div className="relative overflow-hidden" style={{ aspectRatio: '16/10' }}>
                        <img
                          src={post.featuredImage}
                          alt={post.title}
                          loading="lazy"
                          className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105"
                        />
                        <div
                          className="absolute top-3 left-3 px-3 py-1 rounded-full text-xs font-semibold"
                          style={{ backgroundColor: 'var(--primary-main)', color: 'white' }}
                        >
                          {post.category}
                        </div>
                      </div>

                      {/* Content */}
                      <div className="p-4 sm:p-5">
                        <div className="flex items-center gap-3 mb-3 text-xs sm:text-sm" style={{ color: 'var(--text-muted)' }}>
                          <span>{new Date(post.date).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })}</span>
                          <span>•</span>
                          <span className="flex items-center gap-1">
                            <Clock size={14} />
                            {post.readTime}
                          </span>
                        </div>
                        
                        <h2 
                          className="text-lg sm:text-xl font-bold mb-2 line-clamp-2 transition-colors group-hover:text-[var(--primary-main)]" 
                          style={{ color: 'var(--text-main)' }}
                        >
                          {post.title}
                        </h2>
                        
                        <p 
                          className="text-sm mb-4 line-clamp-2" 
                          style={{ color: 'var(--text-muted)' }}
                        >
                          {post.excerpt}
                        </p>

                        <span 
                          className="inline-flex items-center gap-1 text-sm font-semibold transition-colors"
                          style={{ color: 'var(--primary-main)' }}
                        >
                          Read More <ArrowRight size={16} className="transition-transform group-hover:translate-x-1" />
                        </span>
                      </div>
                    </div>
                  </Link>
                </motion.article>
              ))}
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-12 sm:py-16" style={{ backgroundColor: 'white' }}>
          <div className="container mx-auto px-4">
            <div
              className="text-center p-8 sm:p-12 rounded-2xl sm:rounded-3xl"
              style={{ backgroundColor: 'var(--primary-light)' }}
            >
              <h2 className="text-2xl sm:text-3xl font-bold mb-4" style={{ color: 'var(--text-main)' }}>
                Ready to Bring Home a Puppy?
              </h2>
              <p className="mb-6 max-w-2xl mx-auto" style={{ color: 'var(--text-muted)' }}>
                Visit Petsmart Puppies in Sector 31, Noida or contact us on WhatsApp for availability
              </p>
              <div className="flex flex-wrap justify-center gap-4">
                <Link
                  to="/puppies/"
                  className="inline-flex items-center gap-2 px-6 py-3 rounded-full font-bold transition-all hover:-translate-y-1"
                  style={{
                    backgroundColor: 'var(--primary-main)',
                    color: 'white',
                    boxShadow: '0 10px 30px rgba(14, 165, 233, 0.3)'
                  }}
                >
                  View Available Puppies <ArrowRight size={18} />
                </Link>
                <button
                  onClick={handleWhatsApp}
                  className="inline-flex items-center gap-2 px-6 py-3 rounded-full font-bold transition-all hover:-translate-y-1"
                  style={{
                    backgroundColor: '#25D366',
                    color: 'white',
                    border: 'none',
                    cursor: 'pointer',
                    boxShadow: '0 10px 30px rgba(37, 211, 102, 0.3)'
                  }}
                >
                  <MessageCircle size={18} /> Chat on WhatsApp
                </button>
              </div>
            </div>
          </div>
        </section>
      </div>
    </>
  );
};

export default Blog;
